import { isSafeInternalHref } from "@/lib/back-nav";
import {
  isPlausibleReferralCode,
  normalizeReferralCode,
} from "@/lib/referrals/code-format";

/**
 * Stable same-origin endpoint that web OAuth returns to after the provider has
 * created a session. It finalizes the account and redirects without rendering
 * a React page. See `app/(without-navbar)/auth/complete/route.ts`.
 */
export const POST_AUTH_PATH = "/auth/complete";

/** Query param carrying the page the user wanted before they signed in. */
export const POST_AUTH_NEXT_PARAM = "next";

/**
 * Query param carrying a referral code through sign-in.
 *
 * This rides the OAuth `callbackURL`, which is why it works where a cookie does
 * not: Sign in with Apple returns via a cross-site `form_post`, and a
 * SameSite=Lax cookie is not sent on a cross-site POST. Better Auth redirects to
 * the callbackURL afterwards, so the gate sees this param on a normal same-site
 * GET regardless of provider. See the `/auth/complete` Route Handler.
 */
export const POST_AUTH_REF_PARAM = "ref";

/**
 * Query param carrying the *kind* of sign-in that was started.
 *
 * Rides the OAuth `callbackURL` for the same reason `ref` does: Sign in with
 * Apple returns via a cross-site `form_post`, on which a `SameSite=Lax` cookie
 * is not sent, so a cookie cannot carry this across the round-trip.
 */
export const POST_AUTH_FLOW_PARAM = "flow";

/**
 * Sign-in started from a checkout drawer (event / venue booking). Buying is
 * gated on identity, not on a profile, so the gate must hand these users
 * straight back to their purchase instead of into onboarding. See
 * `plans/guest-checkout-auth.md`.
 */
export const CHECKOUT_AUTH_FLOW = "checkout";
export const TICKET_CLAIM_AUTH_FLOW = "ticket-claim";

export type AuthFlow =
  | typeof CHECKOUT_AUTH_FLOW
  | typeof TICKET_CLAIM_AUTH_FLOW;

/** Intent names shared by the in-app phone/native authentication adapters. */
export type PostAuthIntent = "default" | "checkout" | "ticket-claim" | "invite";

/**
 * Marker the gate appends to the destination when it is handing a checkout
 * sign-in back to its basket.
 *
 * The saved basket lives in sessionStorage, which a server render cannot see,
 * so without this the destination has no way to know a resume is inbound and
 * paints a full ticket picker before the client-side resume takes over — the
 * viewer watches their selection reappear and then get replaced. This param is
 * the one bit of that state the server CAN see, so the page can render its
 * "finishing" state from the first byte instead.
 */
export const CHECKOUT_RESUME_PARAM = "resume";
export const CHECKOUT_RESUME_VALUE = "checkout";

/** Append the resume marker to an already-sanitized internal destination. */
export function withCheckoutResumeMarker(destination: string): string {
  const [path, hash] = destination.split("#", 2);
  const separator = path.includes("?") ? "&" : "?";
  const marked = `${path}${separator}${CHECKOUT_RESUME_PARAM}=${CHECKOUT_RESUME_VALUE}`;
  return hash ? `${marked}#${hash}` : marked;
}

/** Remove only the checkout hand-off marker, preserving other query params. */
export function withoutCheckoutResumeMarker(destination: string): string {
  const [pathAndQuery, hash] = destination.split("#", 2);
  const [pathname, query] = pathAndQuery.split("?", 2);
  if (!query) return destination;

  const remaining = Array.from(new URLSearchParams(query).entries()).filter(
    ([key, value]) =>
      key !== CHECKOUT_RESUME_PARAM || value !== CHECKOUT_RESUME_VALUE,
  );
  const search = new URLSearchParams(remaining).toString();
  const cleaned = search ? `${pathname}?${search}` : pathname;
  return hash ? `${cleaned}#${hash}` : cleaned;
}

/** Narrow an untrusted `flow` value to a flow we actually implement. */
export function sanitizeAuthFlow(
  value: string | string[] | null | undefined,
): AuthFlow | null {
  const raw = Array.isArray(value) ? value[0] : value;
  if (raw === CHECKOUT_AUTH_FLOW) return CHECKOUT_AUTH_FLOW;
  if (raw === TICKET_CLAIM_AUTH_FLOW) return TICKET_CLAIM_AUTH_FLOW;
  return null;
}

export function authFlowForIntent(intent: PostAuthIntent): AuthFlow | null {
  if (intent === "checkout") return CHECKOUT_AUTH_FLOW;
  if (intent === "ticket-claim") return TICKET_CLAIM_AUTH_FLOW;
  return null;
}

/** Fallback destination when no safe destination is provided. */
export const DEFAULT_POST_AUTH_DESTINATION = "/hiives";
export const AUTH_ERROR_PATH = "/auth/error";

function pointsAtGate(value: string) {
  return (
    value === POST_AUTH_PATH ||
    value.startsWith(`${POST_AUTH_PATH}?`) ||
    value === "/welcome" ||
    value.startsWith("/welcome?")
  );
}

/**
 * Normalize an intended post-auth destination to a safe internal href,
 * falling back to {@link DEFAULT_POST_AUTH_DESTINATION}. Never resolves to the
 * gate itself, which would otherwise cause a redirect loop.
 */
export function sanitizeDestination(value: string | null | undefined): string {
  if (!isSafeInternalHref(value) || pointsAtGate(value)) {
    return DEFAULT_POST_AUTH_DESTINATION;
  }
  return value;
}

/**
 * Build the post-auth completion URL used as the OAuth `callbackURL`. After
 * sign-in the handler runs the shared finalizer and redirects to its result.
 */
export function buildPostAuthCallbackURL(
  destination?: string | null,
  referralCode?: string | null,
  flow?: AuthFlow | null,
): string {
  const next = sanitizeDestination(destination);
  let url = `${POST_AUTH_PATH}?${POST_AUTH_NEXT_PARAM}=${encodeURIComponent(next)}`;
  const ref = sanitizeReferralParam(referralCode);
  if (ref) {
    url += `&${POST_AUTH_REF_PARAM}=${encodeURIComponent(ref)}`;
  }
  const safeFlow = sanitizeAuthFlow(flow);
  if (safeFlow) {
    url += `&${POST_AUTH_FLOW_PARAM}=${safeFlow}`;
  }
  return url;
}

function isTicketClaimDestination(destination: string): boolean {
  const parsed = new URL(destination, "https://hiive.local");
  return (
    /^\/t\/[A-Za-z0-9-]{1,64}$/.test(parsed.pathname) &&
    parsed.searchParams.get("claim") === "1"
  );
}

/**
 * Decide navigation after the shared server finalizer has ensured a profile
 * row. `profileCreated` is the one-time onboarding boundary; completeness is a
 * separate capability gate used only when somebody explicitly starts an
 * action that requires a finished profile.
 */
export function resolvePostAuthDestination(input: {
  destination?: string | null;
  flow?: AuthFlow | null;
  profileCreated: boolean;
}): string {
  const destination = sanitizeDestination(input.destination);
  const flow = sanitizeAuthFlow(input.flow);

  if (flow === CHECKOUT_AUTH_FLOW) {
    return withCheckoutResumeMarker(destination);
  }

  if (
    flow === TICKET_CLAIM_AUTH_FLOW &&
    isTicketClaimDestination(destination)
  ) {
    return destination;
  }

  return input.profileCreated ? buildOnboardingURL(destination) : destination;
}

/**
 * Codes are uppercase alphanumerics, so anything else is either noise or an
 * injection attempt riding the callback URL. Shares the normaliser with the
 * referral feature so the two can never disagree about what a code looks like.
 */
export function sanitizeReferralParam(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  const cleaned = normalizeReferralCode(value);
  return isPlausibleReferralCode(cleaned) ? cleaned : null;
}

export function buildAuthErrorCallbackURL(destination?: string | null): string {
  const next = sanitizeDestination(destination);
  return `${AUTH_ERROR_PATH}?${POST_AUTH_NEXT_PARAM}=${encodeURIComponent(next)}`;
}

/**
 * Build the onboarding URL that returns the user to `destination` once they
 * finish (consumed by the final onboarding step).
 */
export function buildOnboardingURL(destination?: string | null): string {
  const next = sanitizeDestination(destination);
  return `/onboarding?${POST_AUTH_NEXT_PARAM}=${encodeURIComponent(next)}`;
}
